// scheduling/components/dashboard/MyScheduleView.jsx
// Presentational: the signed-in staffer's rolling 4-week personal schedule + stat
// bar. Extracted from StaffDashboard's "schedule" tab. Data arrives via props from
// the Dashboard owner (useSchedulerData); no fetching / me-resolution here.

import { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BRAND, JOB_TYPE_COLORS, CUSTOM_JOB_CATEGORY_COLORS, CUSTOM_JOB_DEFAULT_COLOR } from '../../utils/schedConstants';
import { computeStaffWeeklyUtil } from '../../utils/capacity';
import { getMonday, toISO, addDays, fmtShort, DOW, utilColor, computeStaffStats } from '../../utils/staffStats';
import SchedStatBar from '../SchedStatBar';
import ClosedJobsSection from './ClosedJobsSection';

export default function MyScheduleView({
  meStaffId, schedule, getDayCap, dayHoursByStaff, closedJobs, clearClosedJobFuture,
}) {
  const navigate = useNavigate();
  const [expandedJob, setExpandedJob] = useState(null); // "weekIdx-jobKey"

  const windowStart = useMemo(() => getMonday(new Date()), []);

  const mySchedule = useMemo(
    () => (schedule || []).filter(e => e.StaffId === meStaffId),
    [schedule, meStaffId],
  );

  // Group my entries into 4 week buckets → jobs per week.
  const weeks = useMemo(() => {
    const result = [];
    for (let w = 0; w < 4; w++) {
      const mon = addDays(windowStart, w * 7);
      const fri = addDays(mon, 4);
      result.push({ mon, fri, entries: [], jobs: {} });
    }
    for (const entry of mySchedule) {
      const d = new Date(entry.EntryDate);
      d.setHours(0, 0, 0, 0);
      const weekIdx = Math.floor((d - windowStart) / (7 * 86400000));
      if (weekIdx < 0 || weekIdx >= 4) continue;
      const week = result[weekIdx];
      week.entries.push(entry);
      // Custom jobs (CPE, Recruiting, …) have no client and no JobId, so they key on
      // CustomJobId. Keying them on ClientCode_JobId gave every custom job the same
      // "_" key, merging them all into one "Unknown Client" card (bug #341).
      const isCustom = !!entry.CustomJobId;
      const jobKey = isCustom
        ? `custom_${entry.CustomJobId}`
        : `${entry.ClientCode || ''}_${entry.JobId || ''}`;
      if (!week.jobs[jobKey]) {
        week.jobs[jobKey] = isCustom ? {
          jobKey, isCustom, clientId: null,
          clientName: entry.CustomJobName || 'Custom job',
          clientStatus: null,
          jobName: entry.CustomJobDescription || 'Custom job',
          jobType: entry.CustomJobCategory || 'Other',
          totalHours: 0, daily: {},
        } : {
          jobKey, isCustom, clientId: entry.ClientId,
          clientName: entry.ClientName || 'Unknown Client',
          clientStatus: entry.ClientStatus || null,
          jobName: entry.JobName || '',
          jobType: entry.ServiceDesc || entry.JobType || entry.JobClassDesc || '',
          totalHours: 0, daily: {},
        };
      }
      const dow = d.getDay();
      const dayLabel = DOW[dow - 1] || '?';
      const job = week.jobs[jobKey];
      job.totalHours += entry.Hours || 0;
      job.daily[dayLabel] = (job.daily[dayLabel] || 0) + (entry.Hours || 0);
    }
    return result;
  }, [mySchedule, windowStart]);

  const utilByWeek = useMemo(() => {
    const weekMondays = [];
    for (let i = 0; i < 4; i++) weekMondays.push(toISO(addDays(windowStart, i * 7)));
    let rows = [];
    try { rows = computeStaffWeeklyUtil({ staffIds: [meStaffId], weekMondays, getDayCap, dayHoursByStaff }); } catch { rows = []; }
    const map = {};
    for (const w of (rows[0]?.weeks || [])) {
      const key = w.weekStart ? w.weekStart.slice(0, 10) : null;
      if (key) map[key] = { CapacityHours: w.capacity, ScheduledHours: w.scheduled, UtilizationPct: w.pct };
    }
    return map;
  }, [meStaffId, windowStart, getDayCap, dayHoursByStaff]);

  const stats = useMemo(
    () => computeStaffStats({ meStaffId, schedule, getDayCap, dayHoursByStaff }),
    [meStaffId, schedule, getDayCap, dayHoursByStaff],
  );

  const toggleExpand = useCallback((key) => {
    setExpandedJob(prev => (prev === key ? null : key));
  }, []);

  const addToSchedule = useCallback(() => {
    sessionStorage.setItem('pendingAssignment', JSON.stringify({ staffIds: [meStaffId], staffNames: [] }));
    navigate('/schedule/grid');
  }, [meStaffId, navigate]);

  return (
    <>
      {clearClosedJobFuture && (
        <ClosedJobsSection
          closedJobs={closedJobs} clearClosedJobFuture={clearClosedJobFuture}
          mode="mine" meStaffId={meStaffId} />
      )}

      <SchedStatBar items={[
        { label: 'Hours This Week', value: stats.thisWeek, color: BRAND.deepTeal },
        { label: 'Hours Next Week', value: stats.nextWeek, color: BRAND.oceanBlue },
        { label: 'Total 4-Week', value: stats.total4, color: BRAND.sageGreen },
        { label: 'Avg Utilization', value: `${stats.avgUtil}%`, color: utilColor(stats.avgUtil) },
      ]} />

      <div className="sched-staff-weeks">
        <div className="sched-staff-addrow">
          <button className="sched-assign-btn"
            title="Manually schedule yourself on a job."
            style={{ fontSize: 12, padding: '7px 14px' }}
            onClick={addToSchedule}>
            + Add to Schedule
          </button>
        </div>

        {mySchedule.length === 0 ? (
          <div className="sched-staff-empty">No assignments scheduled for this period.</div>
        ) : (
          weeks.map((week, wi) => {
            const uKey = toISO(week.mon);
            const uRec = utilByWeek[uKey];
            const capacity = uRec?.CapacityHours ?? 40;
            const scheduled = uRec?.ScheduledHours ?? week.entries.reduce((s, e) => s + (e.Hours || 0), 0);
            const pct = capacity > 0 ? Math.round((scheduled / capacity) * 100) : 0;
            const available = Math.max(0, capacity - scheduled);
            // Client jobs first, custom jobs after; stable sort keeps each group's order.
            const jobs = Object.values(week.jobs).sort((a, b) => a.isCustom - b.isCustom);

            return (
              <div key={wi} className="sched-staff-weekcard">
                <div className="sched-staff-weekcard-header">
                  <div className="sched-staff-weekcard-title">
                    Week of {fmtShort(week.mon)} &ndash; {fmtShort(week.fri)}
                  </div>
                  <div className="sched-staff-weekcard-meta">
                    <span style={{ color: utilColor(pct), fontWeight: 700 }}>{pct}%</span>
                    <span className="sched-staff-weekcard-avail">{available}h available</span>
                  </div>
                </div>

                <div className="sched-staff-utilbar">
                  <div className="sched-staff-utilbar-fill"
                    style={{ width: `${Math.min(pct, 100)}%`, background: utilColor(pct) }} />
                </div>

                {jobs.length === 0 ? (
                  <div className="sched-staff-weekcard-nojobs">No assignments</div>
                ) : (
                  <div className="sched-staff-joblist">
                    {jobs.map(job => {
                      const expandKey = `${wi}-${job.jobKey}`;
                      const isExpanded = expandedJob === expandKey;
                      const typeColor = job.isCustom
                        ? (CUSTOM_JOB_CATEGORY_COLORS[job.jobType] || CUSTOM_JOB_DEFAULT_COLOR)
                        : (JOB_TYPE_COLORS[job.jobType] || { bg: '#E5E7EB', text: '#374151' });
                      return (
                        <div key={job.jobKey}
                          className={`sched-staff-jobcard${job.isCustom ? ' sched-staff-jobcard--custom' : ''}${isExpanded ? ' sched-staff-jobcard--expanded' : ''}`}
                          style={job.isCustom ? { borderLeftColor: typeColor.bg } : undefined}
                          onClick={() => toggleExpand(expandKey)}>
                          <div className="sched-staff-jobcard-row">
                            <div className="sched-staff-jobcard-info">
                              {job.isCustom ? (
                                <div className="sched-staff-jobcard-client" title="Custom job">
                                  {job.clientName}
                                </div>
                              ) : (
                                <div className="sched-staff-jobcard-client sched-mgr-client-link"
                                  onClick={(e) => { e.stopPropagation(); if (job.clientId) navigate(`/schedule/client/${job.clientId}`); }}>
                                  {job.clientName}
                                  {job.clientStatus === 'FINANCIAL' && <span className="sched-client-status-badge sched-client-status-badge--financial" title="Client status: FINANCIAL (Stop Work)">⚠ Financial</span>}
                                  {job.clientStatus === 'LOST' && <span className="sched-client-status-badge sched-client-status-badge--lost" title="Client status: LOST">✕ Lost</span>}
                                </div>
                              )}
                              <div className="sched-staff-jobcard-name">{job.jobName}</div>
                            </div>
                            <div className="sched-staff-jobcard-right">
                              <span className="sched-type-tag" style={{ background: typeColor.bg, color: typeColor.text }}>
                                {job.jobType || 'Other'}
                              </span>
                              <span className="sched-staff-jobcard-hours">
                                {Math.round(job.totalHours * 100) / 100}h
                              </span>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="sched-staff-jobcard-daily">
                              {DOW.map(day => (
                                <div key={day} className="sched-staff-jobcard-day">
                                  <span className="sched-staff-jobcard-day-label">{day}</span>
                                  <span className="sched-staff-jobcard-day-hours">
                                    {job.daily[day] ? `${job.daily[day]}h` : '—'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
